import { canCreateRelation } from "../../domain/vocabulary/relations.ts";
import {
  findMeaningById,
  findWordById,
  findWordByNormalizedText,
  meaningsForWord,
} from "../../domain/vocabulary/grouping.ts";
import { moveMeaningToWord, removeMeaningFromSnapshot, removeWordFromSnapshot, snapshotWithDataRevision } from "../../domain/vocabulary/mutations.ts";
import { normalizeWordInput } from "../../domain/vocabulary/normalize.ts";
import type {
  MeaningCard,
  MeaningRelation,
  ReviewEvent,
  VocabularySnapshot,
  WordEntry,
  WordType,
} from "../../domain/vocabulary/types.ts";
import { SCHEDULER_POLICY_VERSION } from "../../domain/vocabulary/types.ts";
import {
  clearObsoleteExerciseRefs,
  initialPracticeState,
  practiceStateFor,
  resetScheduleForNewContent,
  scheduleSnapshotOf,
} from "../../domain/practice/scheduling.ts";
import { applyReviewEvent } from "../../domain/practice/events.ts";
import { ProductError } from "../../contracts/errors.ts";
import type { Clock, IdGenerator } from "../ports/platform.ts";
import type { CommitResult, VocabularyStore } from "../ports/storage.ts";
import type { MeaningPackage } from "../../contracts/ai/schemas.ts";

export interface EditableMeaningFields {
  displayText: string;
  type: WordType;
  shortLabel: string;
  definition: string;
  russianHint: string | null;
  example: string;
  memoryKey: string | null;
  collocations: string[];
}

function semanticChange(before: MeaningCard, after: EditableMeaningFields, wordChanged: boolean): boolean {
  return (
    wordChanged ||
    before.type !== after.type ||
    before.shortLabel !== after.shortLabel ||
    before.definition !== after.definition ||
    before.example !== after.example ||
    before.russianHint !== after.russianHint ||
    JSON.stringify(before.collocations) !== JSON.stringify(after.collocations)
  );
}

export class VocabularyCommands {
  constructor(
    private readonly store: VocabularyStore,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  async savePackages(input: {
    wordDisplay: string;
    packages: MeaningPackage[];
    existingWordId?: string;
    baseRevision: number;
  }): Promise<CommitResult & { wordId?: string; meaningIds?: string[] }> {
    return this.store.commit((current) => {
      if (current.dataRevision !== input.baseRevision) {
        throw new ProductError(
          "stale_revision",
          "Vocabulary changed in another tab. Review the latest meanings before saving.",
        );
      }
      const now = this.clock.now();
      const normalized = normalizeWordInput(input.wordDisplay);
      let words = [...current.words];
      let word = input.existingWordId
        ? findWordById(current, input.existingWordId)
        : findWordByNormalizedText(current, normalized.normalizedText);
      if (!word) {
        word = {
          id: this.ids.create(),
          normalizedText: normalized.normalizedText,
          displayText: normalized.displayText,
          createdAt: now,
          updatedAt: now,
        };
        words = [...words, word];
      } else {
        words = words.map((item) =>
          item.id === word?.id ? { ...item, updatedAt: now, displayText: word.displayText } : item,
        );
      }
      const existingLabels = current.meanings
        .filter((meaning) => meaning.wordId === word.id)
        .map((meaning) => meaning.shortLabel.trim().toLowerCase());
      const meaningIds: string[] = [];
      const newMeanings: MeaningCard[] = [];
      const newRelations: MeaningRelation[] = [];
      const exerciseSets = [...current.exerciseSets];
      const practiceStates = [...current.practiceStates];

      for (const pack of input.packages) {
        const duplicate = existingLabels.includes(pack.shortLabel.trim().toLowerCase());
        if (duplicate) {
          throw new ProductError(
            "validation_failed",
            `“${pack.shortLabel}” already exists for this word. Remove the duplicate before saving.`,
          );
        }
        const meaningId = this.ids.create();
        meaningIds.push(meaningId);
        existingLabels.push(pack.shortLabel.trim().toLowerCase());
        newMeanings.push({
          id: meaningId,
          wordId: word.id,
          type: pack.type,
          shortLabel: pack.shortLabel,
          definition: pack.definition,
          russianHint: pack.russianHint,
          example: pack.example,
          memoryKey: pack.memoryKey,
          collocations: pack.collocations,
          contentRevision: 1,
          practiceReadiness: "ready",
          createdAt: now,
          updatedAt: now,
        });
        const dependencyRevisions: Record<string, number> = {};
        for (const relation of pack.relations) {
          const target = findMeaningById(current, relation.targetMeaningId);
          if (!target) {
            continue;
          }
          newRelations.push({
            sourceMeaningId: meaningId,
            targetMeaningId: target.id,
            explanation: relation.explanation,
            revision: 1,
          });
          dependencyRevisions[target.id] = target.contentRevision;
        }
        exerciseSets.push({
          meaningId,
          sourceContentRevision: 1,
          dependencyRevisions,
          generationMetadata: {
            model: pack.itemId,
            generatedAt: now,
            promptVersion: "app-save",
          },
          exercises: pack.exercises.map((exercise) => ({
            ...exercise,
            referencedMeaningIds: [meaningId, ...exercise.referencedMeaningIds.filter((id) => id !== meaningId)],
          })),
          status: "ready",
        });
        practiceStates.push(initialPracticeState(meaningId, now));
      }

      return snapshotWithDataRevision(current, {
        words,
        meanings: [...current.meanings, ...newMeanings],
        relations: [...current.relations, ...newRelations],
        exerciseSets,
        practiceStates,
      });
    }).then((result) => {
      if (!result.ok) {
        return result;
      }
      return {
        ...result,
        wordId: findWordByNormalizedText(result.snapshot, normalizeWordInput(input.wordDisplay).normalizedText)?.id,
        meaningIds: result.snapshot.meanings
          .filter((meaning) => meaning.wordId === findWordByNormalizedText(result.snapshot, normalizeWordInput(input.wordDisplay).normalizedText)?.id)
          .slice(-input.packages.length)
          .map((meaning) => meaning.id),
      };
    });
  }

  async saveEditedMeaning(
    meaningId: string,
    fields: EditableMeaningFields,
    relations: Array<{ targetMeaningId: string; explanation: string }>,
    regeneratePackage?: MeaningPackage,
  ): Promise<CommitResult> {
    return this.store.commit((current) => {
      const meaning = findMeaningById(current, meaningId);
      if (!meaning) {
        throw new ProductError("not_found", "This meaning is no longer saved.");
      }
      const now = this.clock.now();
      const normalized = normalizeWordInput(fields.displayText);
      if (!normalized.normalizedText) {
        throw new ProductError("validation_failed", "Enter a word or phrase.");
      }
      let snapshot: VocabularySnapshot = current;
      let word = findWordById(snapshot, meaning.wordId);
      if (!word) {
        throw new ProductError("not_found", "The word for this meaning is missing.");
      }
      const wordChanged = word.normalizedText !== normalized.normalizedText;
      if (wordChanged) {
        let destination = findWordByNormalizedText(snapshot, normalized.normalizedText);
        if (!destination) {
          destination = {
            id: this.ids.create(),
            normalizedText: normalized.normalizedText,
            displayText: normalized.displayText,
            createdAt: now,
            updatedAt: now,
          };
          snapshot = {
            ...snapshot,
            words: [...snapshot.words, destination],
          };
        }
        snapshot = moveMeaningToWord(snapshot, meaningId, destination, now);
        word = destination;
      }
      const latest = findMeaningById(snapshot, meaningId);
      if (!latest) {
        throw new ProductError("not_found", "This meaning is no longer saved.");
      }
      const material = semanticChange(latest, fields, wordChanged);
      const nextMeaning: MeaningCard = {
        ...latest,
        type: fields.type,
        shortLabel: fields.shortLabel.trim(),
        definition: fields.definition.trim(),
        russianHint: fields.russianHint?.trim() ? fields.russianHint.trim() : null,
        example: fields.example.trim(),
        memoryKey: fields.memoryKey?.trim() ? fields.memoryKey.trim() : null,
        collocations: fields.collocations.map((item) => item.trim()).filter(Boolean),
        contentRevision: material ? latest.contentRevision + 1 : latest.contentRevision,
        practiceReadiness: material && !regeneratePackage ? "needs_regeneration" : regeneratePackage ? "ready" : latest.practiceReadiness,
        updatedAt: now,
      };
      const exerciseSets = snapshot.exerciseSets.map((set) => {
        if (set.meaningId !== meaningId) {
          if (material && meaningId in set.dependencyRevisions) {
            return { ...set, status: "invalidated" as const };
          }
          return set;
        }
        if (regeneratePackage) {
          const dependencyRevisions: Record<string, number> = {};
          for (const relation of regeneratePackage.relations) {
            const target = findMeaningById(snapshot, relation.targetMeaningId);
            if (target) {
              dependencyRevisions[target.id] = target.contentRevision;
            }
          }
          return {
            meaningId,
            sourceContentRevision: nextMeaning.contentRevision,
            dependencyRevisions,
            generationMetadata: {
              model: regeneratePackage.itemId,
              generatedAt: now,
              promptVersion: "edit-regenerate",
            },
            exercises: regeneratePackage.exercises,
            status: "ready" as const,
          };
        }
        if (material) {
          return { ...set, status: "invalidated" as const };
        }
        return set;
      });
      const otherRelations = snapshot.relations.filter((relation) => relation.sourceMeaningId !== meaningId);
      const nextRelations: MeaningRelation[] = [...otherRelations];
      for (const relation of relations) {
        const allowed = canCreateRelation(
          { ...snapshot, relations: nextRelations },
          meaningId,
          relation.targetMeaningId,
        );
        if (!allowed.ok && allowed.reason !== "duplicate") {
          continue;
        }
        if (allowed.ok) {
          nextRelations.push({
            sourceMeaningId: meaningId,
            targetMeaningId: relation.targetMeaningId,
            explanation: relation.explanation.trim(),
            revision: 1,
          });
        }
      }
      const meanings = snapshot.meanings.map((item) => {
        if (item.id === meaningId) {
          return nextMeaning;
        }
        if (exerciseSets.some((set) => set.meaningId === item.id && set.status === "invalidated")) {
          return { ...item, practiceReadiness: "needs_regeneration" as const };
        }
        return item;
      });
      const words = snapshot.words.map((item) =>
        item.id === word.id ? { ...item, displayText: normalized.displayText, updatedAt: now } : item,
      );
      let practiceStates = snapshot.practiceStates;
      const currentState = practiceStateFor(snapshot, meaningId);
      if (material) {
        practiceStates = snapshot.practiceStates.some((state) => state.meaningId === meaningId)
          ? snapshot.practiceStates.map((state) =>
              state.meaningId === meaningId ? resetScheduleForNewContent(state, now) : state,
            )
          : [...snapshot.practiceStates, resetScheduleForNewContent(currentState, now)];
      } else if (regeneratePackage) {
        const validIds = new Set(regeneratePackage.exercises.map((exercise) => exercise.id));
        practiceStates = snapshot.practiceStates.map((state) => {
          if (state.meaningId !== meaningId) {
            return state;
          }
          const cleared = clearObsoleteExerciseRefs(state, validIds);
          return {
            ...cleared,
            lastExposureAt: now,
            stateRevision: cleared.stateRevision === state.stateRevision ? state.stateRevision + 1 : cleared.stateRevision,
          };
        });
      }
      return snapshotWithDataRevision(snapshot, {
        words,
        meanings,
        relations: nextRelations,
        exerciseSets,
        practiceStates,
      });
    });
  }

  async recordMeaningExposure(meaningId: string): Promise<CommitResult> {
    return this.store.commit((current) => {
      const meaning = findMeaningById(current, meaningId);
      if (!meaning) {
        return current;
      }
      const now = this.clock.now();
      const state = practiceStateFor(current, meaningId);
      const event: ReviewEvent = {
        format: "written",
        origin: "unspecified",
        eventId: this.ids.create(),
        sessionId: "navigation",
        meaningId,
        exerciseId: null,
        exerciseKind: null,
        meaningContentRevision: meaning.contentRevision,
        expectedStateRevision: state.stateRevision,
        presentedAt: now,
        completedAt: now,
        eventKind: "exposure",
        firstAttemptOutcome: null,
        hintUsed: false,
        scheduledOrExtra: "extra",
        schedulerVersion: SCHEDULER_POLICY_VERSION,
        previousSchedule: scheduleSnapshotOf(state),
        resultingSchedule: scheduleSnapshotOf({
          ...state,
          lastExposureAt: now,
          stateRevision: state.stateRevision + 1,
        }),
      };
      const applied = applyReviewEvent(current, event);
      if (!applied.ok) {
        return current;
      }
      return applied.snapshot;
    });
  }

  async deleteMeaning(meaningId: string): Promise<CommitResult> {
    return this.store.commit((current) => removeMeaningFromSnapshot(current, meaningId));
  }

  async deleteWord(wordId: string): Promise<CommitResult> {
    return this.store.commit((current) => removeWordFromSnapshot(current, wordId));
  }

  getWord(wordId: string): WordEntry | undefined {
    const snapshot = this.store.getSnapshot();
    return snapshot ? findWordById(snapshot, wordId) : undefined;
  }

  getMeanings(wordId: string): MeaningCard[] {
    const snapshot = this.store.getSnapshot();
    return snapshot ? meaningsForWord(snapshot, wordId) : [];
  }
}
